import { useWatch } from "react-hook-form";
import { useEffect } from "react";
import { ValuationsFormStep, ValuationsFormStepProps } from "../valuations-form-step";
import { ControlledTextInput } from "../../../components/forms/controlled-text-input";
import { RadioGroup } from "../../../components/inputs/radio-group";
import { TextLink } from "../../../components/text-link.tsx";
import { ErrorCallout } from "../../../components/alerts/error-callout";
import { FontStyles } from "../../../utils/font-styles";
import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";

export const PersonalDetailsStep = (props: ValuationsFormStepProps & { submissionError?: string | null }) => {
  const form = props.form.form;

  // Watch field values to determine visibility
  const [contactPreference, email, phoneNumber] = useWatch({
    control: form.control,
    name: ["contactPreference", "email", "phoneNumber"],
  });

  // Determine which fields should be visible based on contact preference
  const showEmailField = contactPreference !== "phone";
  const showPhoneField = contactPreference !== "email";

  // Check if fields are empty (trimmed)
  const emailEmpty = !email || email.trim() === "";
  const phoneEmpty = !phoneNumber || phoneNumber.trim() === "";

  // Clear hidden fields and their errors when contact preference changes
  useEffect(() => {
    if (contactPreference === "email") {
      // Switching to email only - clear phone field and its error (phone is now hidden)
      form.setValue("phoneNumber", "", { shouldValidate: false });
      form.clearErrors("phoneNumber");
      // Clear email error if field is empty (validation rules changed)
      if (emailEmpty) form.clearErrors("email");
    } else if (contactPreference === "phone") {
      // Switching to phone only - clear email field and its error (email is now hidden)
      form.setValue("email", "", { shouldValidate: false });
      form.clearErrors("email");
      // Clear phone error if field is empty (validation rules changed)
      if (phoneEmpty) form.clearErrors("phoneNumber");
    } else {
      // "Email & Phone" mode - clear errors on empty fields (validation rules changed)
      if (emailEmpty) form.clearErrors("email");
      if (phoneEmpty) form.clearErrors("phoneNumber");
    }
  }, [contactPreference, form, emailEmpty, phoneEmpty]);

  return (
    <ValuationsFormStep {...props} text="Enter your personal details below." isSubmitting={props.isSubmitting}>
      <div className="flex flex-col gap-6.5 md:gap-7.5">
        <div className="flex flex-col gap-4 md:gap-4.5">
          <div className="flex gap-4 flex-col md:flex-row">
            <ControlledTextInput
              control={props.form.form.control}
              name="firstName"
              placeholder="Enter your first name"
              label="First name"
              showRequiredAsterisk
              className="flex-1"
            />
            <ControlledTextInput
              control={props.form.form.control}
              name="lastName"
              placeholder="Enter your surname"
              label="Surname"
              showRequiredAsterisk
              className="flex-1"
            />
          </div>

          <RadioGroup
            value={contactPreference}
            onValueChange={(value) => form.setValue("contactPreference", value as "none" | "email" | "phone")}
            label="How would you like to be contacted?"
            showRequiredAsterisk
            state={form.formState.errors.contactPreference ? "error" : "default"}
            message={form.formState.errors.contactPreference?.message}
            layout="horizontal"
            options={[
              { value: "none", label: "Email & Phone", id: "contact-preference-none" },
              { value: "email", label: "Email only", id: "contact-preference-email" },
              { value: "phone", label: "Phone only", id: "contact-preference-phone" },
            ]}
          />

          <div className="flex gap-4 flex-col md:flex-row">
            {showEmailField && (
              <ControlledTextInput
                control={props.form.form.control}
                name="email"
                placeholder="Enter your email address"
                label="Email address"
                showRequiredAsterisk
                className="w-full md:w-1/2"
                inputMode="email"
              />
            )}
            {showPhoneField && (
              <ControlledTextInput
                control={props.form.form.control}
                name="phoneNumber"
                placeholder="Enter your telephone number"
                label="Phone number"
                showRequiredAsterisk
                className="w-full md:w-1/2"
                inputMode="tel"
              />
            )}
          </div>
        </div>
        <div className={`${FontStyles["body-md"]} text-light-slate`}>
          By providing this information, you consent to us providing it to the selected solicitors so that they can
          contact you directly about your property valuation. Please read our{" "}
          <TextLink href="/privacy-policy/terms-of-use">terms & conditions</TextLink>.
        </div>
      </div>

      {/* Form submission error - shown only when active */}
      {props.submissionError && (
        <ErrorCallout style="outlined">
          <i className={`fa-sharp fa-regular fa-circle-exclamation mr-2 ${FontStyles["body-s"]}`} />
          Sorry, something went wrong while processing your request. Please try submitting the form again, or{" "}
          <TextLink href="mailto:support@espc.com">contact our support team</TextLink> for assistance.
          {props.submissionError.includes("CAPTCHA") && (
            <>
              <br />
              Error details: captcha validation failure
            </>
          )}
          {props.submissionError.includes("Session expired") && (
            <>
              <br />
              Error details: CSRF validation failure
            </>
          )}
        </ErrorCallout>
      )}
    </ValuationsFormStep>
  );
};
